// Fleet-specific React Hooks
// Provides easy-to-use hooks for fleet operator-related API calls
import { useApi, useApiState, usePagination, useApiMutation } from './useApi.js';
import { fleetService } from '../services';

// Fleet Profile Hooks
export const useFleetProfile = (enabled = true) => {
  // Only fetch when enabled (e.g. skip on hospital dashboard so fleet endpoint is not hit)
  return useApi(fleetService.getMyProfile.bind(fleetService), enabled === true ? [] : [enabled]);
};

export const useUpdateFleetProfile = () => {
  return useApiMutation();
};

export const useFleetProfileById = (operatorId) => {
  return useApi(
    fleetService.getProfileById.bind(fleetService, operatorId),
    [operatorId]
  );
};

// Fleet Dashboard Hooks
export const useFleetDashboard = (params = {}) => {
  return useApi(
    fleetService.getMyDashboard.bind(fleetService, params),
    [params]
  );
};

export const useFleetDashboardById = (operatorId, params = {}) => {
  return useApi(
    fleetService.getDashboardById.bind(fleetService, operatorId, params),
    [operatorId, params]
  );
};

// Fleet Operators Hooks
export const useFleetOperators = (params = {}) => {
  return usePagination(fleetService.getAllOperators.bind(fleetService), params);
};

export const useFleetOperatorById = (operatorId) => {
  return useApi(
    fleetService.getOperatorById.bind(fleetService, operatorId),
    [operatorId]
  );
};

// Fleet Analytics Hooks
export const useFleetAnalytics = (params = {}) => {
  return useApi(
    fleetService.getAnalytics.bind(fleetService, params),
    [params]
  );
};

export const usePerformanceMetrics = (params = {}) => {
  return useApi(
    fleetService.getPerformanceMetrics.bind(fleetService, params),
    [params]
  );
};

export const useRevenueAnalytics = (params = {}) => {
  return useApi(
    fleetService.getRevenueAnalytics.bind(fleetService, params),
    [params]
  );
};

export const useUtilizationMetrics = (params = {}) => {
  return useApi(
    fleetService.getUtilizationMetrics.bind(fleetService, params),
    [params]
  );
};

// Fleet Drivers Hooks
export const useFleetDrivers = (params = {}) => {
  return usePagination(fleetService.getMyDrivers.bind(fleetService), params);
};

export const useDriverById = (driverId) => {
  return useApi(
    fleetService.getDriverById.bind(fleetService, driverId),
    [driverId]
  );
};

export const useAddDriver = () => {
  return useApiMutation();
};

export const useUpdateDriver = () => {
  return useApiMutation();
};

export const useRemoveDriver = () => {
  return useApiMutation();
};

export const useUpdateDriverStatus = () => {
  return useApiMutation();
};

export const useDriverPerformance = (driverId, params = {}) => {
  return useApi(
    fleetService.getDriverPerformance.bind(fleetService, driverId, params),
    [driverId, params]
  );
};

// Fleet Ambulances Hooks
export const useFleetAmbulances = (params = {}) => {
  return usePagination(fleetService.getMyAmbulances.bind(fleetService), params);
};

export const useAmbulanceById = (ambulanceId) => {
  return useApi(
    fleetService.getAmbulanceById.bind(fleetService, ambulanceId),
    [ambulanceId]
  );
};

export const useAddAmbulance = () => {
  return useApiMutation();
};

export const useUpdateAmbulance = () => {
  return useApiMutation();
};

export const useRemoveAmbulance = () => {
  return useApiMutation();
};

export const useUpdateAmbulanceStatus = () => {
  return useApiMutation();
};

export const useAssignDriverToAmbulance = () => {
  return useApiMutation();
};

export const useRemoveDriverFromAmbulance = () => {
  return useApiMutation();
};

// Fleet Bookings Hooks
export const useFleetBookings = (params = {}) => {
  return usePagination(fleetService.getMyBookings.bind(fleetService), params);
};

export const useFleetBookingById = (bookingId) => {
  return useApi(
    fleetService.getBookingById.bind(fleetService, bookingId),
    [bookingId]
  );
};

export const useUpdateFleetBookingStatus = () => {
  return useApiMutation();
};

export const useAssignDriverToBooking = () => {
  return useApiMutation();
};

export const useAssignAmbulanceToBooking = () => {
  return useApiMutation();
};

export const useFleetBookingAnalytics = (params = {}) => {
  return useApi(
    fleetService.getBookingAnalytics.bind(fleetService, params),
    [params]
  );
};

// Fleet Reports Hooks
export const useGenerateFleetReport = () => {
  return useApiMutation();
};

export const useFleetReportHistory = (params = {}) => {
  return usePagination(fleetService.getReportHistory.bind(fleetService), params);
};

export const useDownloadFleetReport = () => {
  return useApiMutation();
};

// Fleet Settings Hooks
export const useFleetSettings = () => {
  return useApi(fleetService.getSettings.bind(fleetService));
};

export const useUpdateFleetSettings = () => {
  return useApiMutation();
};

export const useServiceAreas = () => {
  return useApi(fleetService.getServiceAreas.bind(fleetService));
};

export const useUpdateServiceAreas = () => {
  return useApiMutation();
};

export const useFleetEmergencyContacts = () => {
  return useApi(fleetService.getEmergencyContacts.bind(fleetService));
};

export const useUpdateFleetEmergencyContacts = () => {
  return useApiMutation();
};

// Fleet Notifications Hooks
export const useFleetNotifications = (params = {}) => {
  return usePagination(fleetService.getNotifications.bind(fleetService), params);
};

export const useMarkFleetNotificationAsRead = () => {
  return useApiMutation();
};

export const useMarkAllFleetNotificationsAsRead = () => {
  return useApiMutation();
};

// Fleet Maintenance Hooks
export const useMaintenanceSchedule = (params = {}) => {
  return useApi(
    fleetService.getMaintenanceSchedule.bind(fleetService, params),
    [params]
  );
};

export const useScheduleMaintenance = () => {
  return useApiMutation();
};

export const useUpdateMaintenanceStatus = () => {
  return useApiMutation();
};

export const useMaintenanceHistory = (params = {}) => {
  return usePagination(fleetService.getMaintenanceHistory.bind(fleetService), params);
};

// Combined Fleet State Hook
export const useFleetState = () => {
  const profile = useFleetProfile();
  const dashboard = useFleetDashboard();
  const analytics = useFleetAnalytics();
  const drivers = useFleetDrivers();
  const ambulances = useFleetAmbulances();
  const bookings = useFleetBookings();
  const notifications = useFleetNotifications();
  const settings = useFleetSettings();

  return {
    profile,
    dashboard,
    analytics,
    drivers,
    ambulances,
    bookings,
    notifications,
    settings
  };
};
